/**
 * OtelStatusPanel — C072 OpenTelemetry 관측 status 패널
 * 계약: docs/common-specs/eraf-actuator/C072-opentelemetry.md §Δ.4/§Δ.7
 * Kafka/RabbitMQ/Elasticsearch/Mongo 패널과 동형 구조이나 **입력 폼이 없는 순수 status 패널**이다
 * (관측 데모라 왕복 발행/저장 UI 대신 조회 + 안내 위주). [상태 새로고침] 을 누를 때마다
 * recentTraceId 가 매번 새로 갱신되는 것 자체가 "이 조회 호출도 하나의 span 이다" 라는 시연이다.
 * 5상태: loading(status skeleton) / error(조회 실패) / default(enabled + traceId + hint) /
 *        disabled(enabled=false graceful 안내) / dialog(N/A — 인라인, 입력 폼 없음)
 */
import { useTranslation } from 'react-i18next';
import { useOtelStatus } from '../hooks/useOtelDemo';
import type { ApiError } from '../types/showcase';

export function OtelStatusPanel() {
  const { t } = useTranslation();
  const statusQuery = useOtelStatus();

  const status = statusQuery.data;
  const isLoading = statusQuery.isLoading;
  const isError = statusQuery.isError;
  const isFetching = statusQuery.isFetching;

  return (
    <section aria-labelledby="otel-panel-title">
      <h2
        id="otel-panel-title"
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--spacing-2)',
          marginTop: 0,
        }}
      >
        {t('showcase.otel.title')}
      </h2>
      <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--spacing-6)' }}>
        {t('showcase.otel.subtitle')}
      </p>

      {/* status — loading skeleton */}
      {isLoading && (
        <div aria-busy="true" aria-label={t('showcase.otel.status.loadingAriaLabel')} style={cardStyle}>
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              style={{
                height: 16,
                width: `${35 + ((i * 15) % 40)}%`,
                borderRadius: 'var(--radius-sm)',
                background: 'var(--color-surface-elevated)',
                animation: 'pulse 1.5s ease-in-out infinite',
                marginBottom: 'var(--spacing-2)',
              }}
            />
          ))}
        </div>
      )}

      {/* status — error */}
      {isError && (
        <div role="alert" style={{ ...cardStyle, border: '1px solid var(--color-danger)' }}>
          <p style={{ margin: 0, color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)' }}>
            {(statusQuery.error as ApiError)?.message ?? t('showcase.otel.status.loadFailed')}
          </p>
          <button type="button" onClick={() => void statusQuery.refetch()} style={secondaryButtonStyle}>
            {t('showcase.common.retry')}
          </button>
        </div>
      )}

      {/* status — default (enabled or disabled graceful) */}
      {!isLoading && !isError && status && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
          <div style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-4)' }}>
              <h3 style={{ ...sectionTitleStyle, marginBottom: 0 }}>{t('showcase.otel.status.title')}</h3>
              <button
                type="button"
                onClick={() => void statusQuery.refetch()}
                disabled={isFetching}
                aria-busy={isFetching}
                style={secondaryButtonStyle}
              >
                {isFetching ? t('showcase.otel.status.refreshing') : t('showcase.otel.status.refreshButton')}
              </button>
            </div>

            <div style={infoGridStyle}>
              <InfoField label={t('showcase.otel.status.enabled')}>
                <span style={{ color: status.enabled ? 'var(--color-status-success)' : 'var(--color-status-error)', fontWeight: 700 }}>
                  {status.enabled ? t('showcase.otel.status.enabledYes') : t('showcase.otel.status.enabledNo')}
                </span>
              </InfoField>
              <InfoField label={t('showcase.otel.status.endpoint')}>
                <code style={monoStyle}>{status.endpoint ?? t('showcase.otel.status.endpointNone')}</code>
              </InfoField>
              <InfoField label={t('showcase.otel.status.serviceName')}>
                <code style={monoStyle}>{status.serviceName}</code>
              </InfoField>
            </div>

            {!status.enabled && (
              <p style={{ marginTop: 'var(--spacing-3)', fontSize: 'var(--font-size-xs)', color: 'var(--color-warning)' }}>
                {t('showcase.otel.disabledNotice')}
              </p>
            )}
          </div>

          {/* recentTraceId — 이 조회 자체가 span 을 생성한다는 시연 */}
          <div style={cardStyle}>
            <h3 style={sectionTitleStyle}>{t('showcase.otel.trace.title')}</h3>
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-3)' }}>
              {t('showcase.otel.trace.description')}
            </p>
            <pre
              role="region"
              aria-label={t('showcase.otel.trace.traceIdAriaLabel')}
              style={{
                margin: 0,
                padding: 'var(--spacing-3)',
                background: 'var(--color-surface-elevated)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontFamily: 'var(--font-family-mono)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-primary)',
                overflowX: 'auto',
              }}
            >
              {status.recentTraceId}
            </pre>
          </div>

          {/* jaegerQueryHint — 운영자용 curl 안내 */}
          <div style={cardStyle}>
            <h3 style={sectionTitleStyle}>{t('showcase.otel.hint.title')}</h3>
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-3)' }}>
              {t('showcase.otel.hint.description')}
            </p>
            <pre
              role="region"
              aria-label={t('showcase.otel.hint.hintAriaLabel')}
              style={{
                margin: 0,
                padding: 'var(--spacing-3)',
                background: 'var(--color-surface-elevated)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontFamily: 'var(--font-family-mono)',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-primary)',
                overflowX: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}
            >
              {status.jaegerQueryHint}
            </pre>
          </div>
        </div>
      )}

      {/* Learning */}
      <div
        style={{
          marginTop: 'var(--spacing-4)',
          padding: 'var(--spacing-3)',
          background: 'var(--color-surface-elevated)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-secondary)',
        }}
      >
        <span aria-hidden="true" style={{ marginRight: 'var(--spacing-2)' }}>📚</span>
        <strong>{t('showcase.panel.learning')}</strong> {t('showcase.otel.learning')}
      </div>
    </section>
  );
}

function InfoField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)' }}>{label}</p>
      <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>{children}</div>
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  padding: 'var(--spacing-5)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-surface-background)',
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-base)',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
  marginBottom: 'var(--spacing-4)',
};

const infoGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
  gap: 'var(--spacing-4)',
};

const monoStyle: React.CSSProperties = {
  fontFamily: 'var(--font-family-mono)',
  fontSize: 'var(--font-size-sm)',
};

const secondaryButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-1) var(--spacing-3)',
  background: 'var(--color-surface-elevated)',
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-xs)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};
